import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Search, ArrowRight, Sparkles, UserPlus, X, Loader2, Check, Wand2, KeyRound, Trash2 } from "lucide-react";
import { SetupWizardModal, SlaChip } from "../components/SetupWizardModal";
import { WalletHealthCard } from "../components/admin/WalletHealthCard";

/**
 * SuperAdmin — the founder's console. Two workflows:
 *
 * 1. PITCH FIRST: "Create prospect" spins up an org with no login and no
 *    billing. Design their pass, walk into the pitch with it already built.
 * 2. CLOSE: hit "Activate" on the prospect — set THEIR pricing on the spot
 *    (charge or waive the $199 setup, custom monthly, optional 30-day trial),
 *    a login is created and credentials handed over live or emailed.
 */
export default function SuperAdmin() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all"); // all | prospect | live | attention
  const [sort, setSort] = useState("recent"); // recent | pass_holders | name
  const [pending, setPending] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [activating, setActivating] = useState(null);
  const [setupOrg, setSetupOrg] = useState(null);
  const { impersonateOrg } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const statusOf = (o) => (o.pipeline_status === "prospect" ? "prospect" : (o.subscription?.status || "trial"));
  const segOf = (o) => { const st = statusOf(o); if (st === "prospect") return "prospect"; if (["active", "trialing"].includes(st)) return "live"; return "attention"; };

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    let out = data.organisations.filter((o) => (seg === "all" || segOf(o) === seg) && (!term
      || o.name?.toLowerCase().includes(term) || o.slug?.toLowerCase().includes(term) || (o.contact_email || "").toLowerCase().includes(term)));
    if (sort === "pass_holders") out = [...out].sort((a, b) => (b.pass_holders || 0) - (a.pass_holders || 0));
    else if (sort === "name") out = [...out].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else out = [...out].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [data, q, seg, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-[13px] text-[#3a3d44]/70 animate-pulse">Loading platform overview…</div>;

  const counts = data.organisations.reduce((m, o) => ({ ...m, [segOf(o)]: (m[segOf(o)] || 0) + 1 }), {});
  const overdue = data.organisations.filter((o) => !o.is_demo && o.setup && !o.setup.complete && o.setup.hours_left != null && o.setup.hours_left < 0).length;

  const takeOver = async (org, orgName) => {
    const id = typeof org === "string" ? org : org.id;
    const name = orgName || org.name;
    setPending(id);
    try { await impersonateOrg(id); toast.success(`Managing ${name}`); nav("/app/programs"); }
    catch (e) { toast.error(apiError(e)); setPending(null); }
  };
  const copyEmail = (email) => { if (!email) return; navigator.clipboard.writeText(email).then(() => toast.success("Email copied"), () => {}); };
  const deleteMerchant = async (org) => {
    const isProspect = org.pipeline_status === "prospect";
    if (isProspect) { if (!window.confirm(`Delete prospect "${org.name}"? Their pass and everything you built will be removed.`)) return; }
    else {
      const typed = window.prompt(`This permanently deletes "${org.name}" — their pass, customers, transactions and login.\n\nType the business name to confirm:`);
      if (typed === null) return;
      if (typed.trim() !== org.name) { toast.error("Name didn't match — nothing was deleted."); return; }
    }
    try { await api.delete(`/admin/merchants/${org.id}${isProspect ? "" : "?force=true"}`); toast.success(`${org.name} removed`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statusPill = (st) => ({
    active: ["Active", "bg-[#C8FF4D] text-[#0B0D10]"], trialing: ["Trial", "bg-[#C8FF4D]/60 text-[#0B0D10]"],
    prospect: ["Prospect", "bg-[#6474FF]/12 text-[#4353E8]"], past_due: ["Past due", "bg-amber-100 text-amber-900"],
    canceled: ["Cancelled", "bg-[#EFEBE1] text-[#25282E]/70"],
  }[st] || [st, "bg-[#EFEBE1] text-[#3a3d44]"]);

  const kpis = [
    ["Merchants", data.total_orgs, `${counts.live || 0} live · ${counts.prospect || 0} prospects`],
    ["Paying / trialing", data.active_subs, `${data.setup_paid_count} setup fees paid`],
    ["MRR", `A$${data.mrr.toLocaleString()}`, "recurring, ex. setup fees"],
    ["Pass holders", (data.total_pass_holders || 0).toLocaleString(), "across every merchant"],
    ["Setup overdue", overdue, overdue ? "past the 48h promise" : "everyone inside 48h"],
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Super Admin" subtitle="Merchants, pipeline and revenue across the platform."
        action={<button data-testid="admin-create-merchant" onClick={() => setCreateOpen(true)}
          className="btn-lift bg-[#0B0D10] text-white rounded-full h-10 px-4 text-[13px] font-semibold inline-flex items-center gap-1.5">
          <UserPlus size={14} strokeWidth={2.4} /> New business
        </button>} />

      <div className="rounded-2xl bg-[#0B0D10] text-white grain relative overflow-hidden" data-testid="admin-kpis">
        <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C8FF4D]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 grid grid-cols-2 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          {kpis.map(([label, value, caption], i) => (
            <div key={label} className={`px-5 py-5 ${i === 4 && overdue ? "bg-[#FF7A3D]/10" : ""}`} data-testid={`admin-kpi-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</div>
              <div className={`font-display font-extrabold tracking-[-0.03em] text-[30px] leading-none mt-3 tabular-nums ${i === 4 && overdue ? "text-[#FF9A6A]" : ""}`}>{value}</div>
              <div className="text-[11.5px] text-white/45 mt-2">{caption}</div>
            </div>
          ))}
        </div>
      </div>

      <WalletHealthCard />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#3a3d44]/50" />
          <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, slug or email…"
            className="w-full h-10 pl-9 pr-3 rounded-full bg-white border border-[#E4E0D6] focus:border-[#0B0D10] focus:outline-none text-[13.5px]" />
        </div>
        <div className="flex items-center gap-1 rounded-full bg-[#EDE9DF] border border-[#0B0D10]/[0.06] p-1">
          {[["all", "All", data.total_orgs], ["live", "Live", counts.live || 0], ["prospect", "Prospects", counts.prospect || 0], ["attention", "Attention", counts.attention || 0]].map(([id, label, n]) => (
            <button key={id} data-testid={`admin-seg-${id}`} onClick={() => setSeg(id)}
              className={`px-3 h-8 rounded-full text-[12px] font-medium transition-colors ${seg === id ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#3a3d44]/60 hover:text-[#0B0D10]"}`}>
              {label}<span className="ml-1.5 tabular-nums text-[#3a3d44]/45">{n}</span>
            </button>
          ))}
        </div>
        <select data-testid="admin-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto h-9 rounded-full bg-white border border-[#E4E0D6] px-3 text-[12.5px] font-medium focus:outline-none focus:border-[#0B0D10]">
          <option value="recent">Newest first</option><option value="pass_holders">Most members</option><option value="name">A–Z</option>
        </select>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#E8E5DE] p-12 text-center text-[13px] text-[#3a3d44]/60">No merchants match.</div>
      ) : (
        <div className="rounded-2xl bg-white border border-[#0B0D10]/10 overflow-x-auto" data-testid="admin-merchant-list">
          <table className="w-full min-w-[860px] text-[13.5px]">
            <thead className="text-left font-mono text-[10px] uppercase tracking-[0.16em] text-[#0B0D10]/50 border-b border-[#0B0D10]/[0.06]">
              <tr><th className="px-5 py-3">Business</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 hidden lg:table-cell">Setup</th><th className="px-4 py-3 hidden sm:table-cell text-right">Members</th><th className="px-4 py-3 hidden md:table-cell text-right">Passes</th><th className="px-4 py-3 hidden lg:table-cell">Plan</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-[#0B0D10]/[0.05]">
              {rows.map((o) => {
                const isProspect = o.pipeline_status === "prospect";
                const st = statusOf(o); const [pillLabel, pillCls] = statusPill(st);
                const sub = o.subscription || {};
                const initials = (o.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <tr key={o.id} className="group hover:bg-[#F4F1E9]/60 transition-colors" data-testid={`admin-org-${o.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`h-9 w-9 rounded-xl font-display font-extrabold grid place-items-center shrink-0 text-[12px] ${isProspect ? "bg-[#6474FF]/12 text-[#4353E8]" : "bg-[#0B0D10] text-[#C8FF4D]"}`}>{initials}</div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[14px] truncate flex items-center gap-2">{o.name}{o.is_demo && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#3a3d44]/50 border border-[#E4E0D6] rounded px-1">demo</span>}</div>
                          <div className="text-[11.5px] text-[#3a3d44]/55 truncate flex items-center gap-1.5">
                            {o.contact_email ? <button onClick={() => copyEmail(o.contact_email)} className="hover:text-[#0B0D10] truncate" data-testid={`admin-copy-email-${o.id}`}>{o.contact_email}</button> : <span>{isProspect ? "No login yet" : "No email"}</span>}
                            {o.created_at && <span className="text-[#3a3d44]/35">· {relativeTime(o.created_at)}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5"><span className={`inline-block font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${pillCls}`}>{pillLabel}</span></td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      {!o.is_demo && o.setup ? (
                        <button data-testid={`admin-setup-${o.id}`} onClick={() => setSetupOrg(o)} className="inline-flex items-center gap-2 group/setup">
                          <span className="h-1.5 w-16 rounded-full bg-[#EDE9DF] overflow-hidden"><span className="block h-full bg-[#0B0D10]" style={{ width: `${(o.setup.done / o.setup.total) * 100}%` }} /></span>
                          <span className="text-[11.5px] tabular-nums text-[#3a3d44]/70 group-hover/setup:text-[#0B0D10]">{o.setup.done}/{o.setup.total}</span>
                          <SlaChip hoursLeft={o.setup.hours_left} complete={o.setup.complete} compact onLight />
                        </button>
                      ) : <span className="text-[#3a3d44]/30">—</span>}
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-right font-display font-bold tabular-nums">{(o.pass_holders || 0).toLocaleString()}</td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-right tabular-nums text-[#3a3d44]/80">{o.live_programs || 0}<span className="text-[#3a3d44]/40"> / {o.programs || 0}</span></td>
                    <td className="px-4 py-3.5 hidden lg:table-cell text-[12.5px] text-[#3a3d44]/75 tabular-nums">
                      {isProspect ? <span className="text-[#3a3d44]/45">Not billed</span> : <>A${sub.price ?? "—"}/mo{sub.trial_days > 0 && <span className="text-[#3a3d44]/45"> · {sub.trial_days}d trial</span>}{o.setup_fee_charged && <span className="text-[#3a3d44]/45"> · setup paid</span>}</>}
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        {isProspect && (
                          <button data-testid={`admin-activate-${o.id}`} onClick={() => setActivating(o)} className="btn-lift bg-[#C8FF4D] text-[#0B0D10] rounded-full h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5"><KeyRound size={11} strokeWidth={2.4} /> Activate</button>
                        )}
                        <button data-testid={`admin-manage-${o.id}`} disabled={pending === o.id} onClick={() => takeOver(o)} className="btn-lift bg-[#0B0D10] text-white rounded-full h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                          {pending === o.id ? "Opening…" : <>Open <ArrowRight size={12} /></>}
                        </button>
                        {!o.is_demo && (
                          <button data-testid={`admin-delete-${o.id}`} onClick={() => deleteMerchant(o)} title="Delete" className="h-8 w-8 rounded-full grid place-items-center text-[#3a3d44]/35 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateMerchantModal open={createOpen} onClose={() => { setCreateOpen(false); load(); }}
        onDesign={(orgId, name) => { setCreateOpen(false); takeOver(orgId, name); }} />
      <ActivateMerchantModal org={activating} onClose={() => { setActivating(null); load(); }} />
      <SetupWizardModal org={setupOrg} onClose={() => { setSetupOrg(null); load(); }}
        onDesign={(o) => { setSetupOrg(null); takeOver(o); }}
        onActivate={(o) => { setSetupOrg(null); setActivating(o); }} />
    </div>
  );
}

// ---------- shared pricing controls ----------
function summaryOf(v) {
  const bits = [];
  bits.push(v.setup_fee > 0 ? `A$${v.setup_fee} setup` : "Setup waived");
  bits.push(`A$${v.monthly_price}/mo`);
  if (v.trial_days > 0) bits.push(`${v.trial_days}-day free trial`);
  return bits.join(" · ");
}

export const DEFAULT_TERMS = { monthly_price: 49.99, setup_fee: 199, trial_days: 0 };

function PricingControls({ value, onChange, testPrefix = "pc" }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const chip = (active) => `px-3 h-8 rounded-full text-[12px] font-semibold transition-colors ${active ? "bg-[#0B0D10] text-white" : "bg-white border border-[#E4E0D6] text-[#3a3d44] hover:border-[#0B0D10]/40"}`;
  return (
    <div className="mt-5 rounded-2xl bg-[#EFEBE1]/70 border border-[#E4E0D6] p-4" data-testid={`${testPrefix}-pricing`}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium mb-3">Pricing</div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-[#3a3d44]/70 w-[62px]">Monthly</span>
        <button type="button" data-testid={`${testPrefix}-price-standard`} className={chip(value.monthly_price === 49.99)} onClick={() => set({ monthly_price: 49.99 })}>$49.99</button>
        <div className="flex items-center gap-1 text-[12px] text-[#3a3d44]/70">
          <span>A$</span>
          <input type="number" step="0.01" min="0" data-testid={`${testPrefix}-price-custom`}
            value={value.monthly_price}
            onChange={(e) => set({ monthly_price: parseFloat(e.target.value) || 0 })}
            className="w-[76px] h-8 rounded-lg bg-white border border-[#E4E0D6] px-2 text-[13px] focus:border-[#0B0D10] outline-none tabular-nums" />
          <span>/mo</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2.5">
        <span className="text-[12px] text-[#3a3d44]/70 w-[62px]">Setup fee</span>
        <button type="button" data-testid={`${testPrefix}-setup-199`} className={chip(value.setup_fee === 199)} onClick={() => set({ setup_fee: 199 })}>Charge $199</button>
        <button type="button" data-testid={`${testPrefix}-setup-waive`} className={chip(value.setup_fee === 0)} onClick={() => set({ setup_fee: 0 })}>Waive it</button>
        <div className="flex items-center gap-1 text-[12px] text-[#3a3d44]/70">
          <span>A$</span>
          <input type="number" step="1" min="0" data-testid={`${testPrefix}-setup-custom`}
            value={value.setup_fee}
            onChange={(e) => set({ setup_fee: parseFloat(e.target.value) || 0 })}
            className="w-[76px] h-8 rounded-lg bg-white border border-[#E4E0D6] px-2 text-[13px] focus:border-[#0B0D10] outline-none tabular-nums" />
        </div>
      </div>
      <label className="mt-3 flex items-center gap-2 text-[13px] cursor-pointer select-none">
        <input type="checkbox" data-testid={`${testPrefix}-trial`} checked={value.trial_days > 0} onChange={(e) => set({ trial_days: e.target.checked ? 30 : 0 })} />
        <span className="text-[#3a3d44]">30-day free trial</span>
      </label>
      <div className="mt-3 text-[12.5px] font-semibold text-[#0B0D10] bg-[#C8FF4D]/70 rounded-lg px-3 py-1.5 inline-block" data-testid={`${testPrefix}-summary`}>
        {summaryOf(value)}
      </div>
    </div>
  );
}

// ---------- credentials success card (shared by create + activate) ----------
function CredentialsCard({ result }) {
  return (
    <div className="mt-5 rounded-2xl bg-[#0B0D10] text-white p-5">
      <div className="text-[11px] uppercase tracking-[0.14em] text-[#C8FF4D] mb-2">Temporary credentials</div>
      <div className="text-[12.5px] text-white/60 mb-1">Email</div>
      <div className="font-mono text-[14px] font-medium mb-3">{result.email}</div>
      <div className="text-[12.5px] text-white/60 mb-1">Password (one-time)</div>
      <div className="flex items-center gap-2">
        <div className="font-mono text-[14px] font-semibold bg-white/[0.06] px-3 py-1.5 rounded-lg inline-block">{result.temporary_password}</div>
        <button onClick={() => { navigator.clipboard.writeText(result.temporary_password).then(() => toast.success("Password copied"), () => toast.error("Couldn't copy — select it manually")); }} className="text-[12px] font-medium text-white/85 hover:text-white">Copy</button>
      </div>
      {result.email_sent ? (
        <p className="text-[11.5px] text-white/50 mt-3">Welcome email sent. Reply-to is your inbox.</p>
      ) : (
        <p className="text-[11.5px] text-amber-300/85 mt-3">Email not sent — share these manually.</p>
      )}
    </div>
  );
}

// ---------- Create modal: prospect (pitch first) or full hand-over ----------
function CreateMerchantModal({ open, onClose, onDesign }) {
  const [mode, setMode] = useState("prospect"); // prospect | active
  const [form, setForm] = useState({ name: "", email: "", business_name: "", phone: "", website: "" });
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", business_name: "", phone: "", website: "" });
      setTerms(DEFAULT_TERMS); setResult(null); setSendEmail(true); setMode("prospect");
    }
  }, [open]);

  const submit = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required."); return; }
    if (mode === "active" && (!form.name.trim() || !form.email.trim())) {
      toast.error("Owner name and email are required to hand over an account."); return;
    }
    setBusy(true);
    try {
      const payload = {
        mode,
        business_name: form.business_name,
        phone: form.phone || null,
        website: form.website || null,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (mode === "active") {
        Object.assign(payload, { name: form.name, send_welcome_email: sendEmail, ...terms });
      }
      const { data } = await api.post("/admin/merchants", payload);
      setResult({ ...data, mode });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!open) return null;

  const tab = (id, label, hint) => (
    <button type="button" data-testid={`cm-mode-${id}`} onClick={() => setMode(id)}
      className={`flex-1 rounded-xl px-3 py-2.5 text-left border transition-colors ${mode === id ? "bg-[#0B0D10] text-white border-[#0B0D10]" : "bg-white border-[#E4E0D6] text-[#3a3d44] hover:border-[#0B0D10]/40"}`}>
      <div className="text-[13px] font-bold">{label}</div>
      <div className={`text-[11px] mt-0.5 ${mode === id ? "text-white/55" : "text-[#3a3d44]/55"}`}>{hint}</div>
    </button>
  );

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0B0D10]/60 backdrop-blur-sm"
        onClick={onClose} data-testid="create-merchant-modal">
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[560px] max-h-[92vh] overflow-y-auto bg-[#F4F1E9] rounded-[24px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)]">

          <div className="bg-[#0B0D10] text-white px-6 py-5 flex items-center justify-between sticky top-0 z-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#C8FF4D]">Admin</div>
              <div className="font-display font-extrabold text-[19px] tracking-tight mt-0.5">New business</div>
            </div>
            <button data-testid="create-merchant-close" onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center bg-white/[0.08] hover:bg-white/[0.15]"><X size={16} /></button>
          </div>

          <div className="p-6 sm:p-8">
            {!result ? (
              <>
                <div className="flex gap-2.5 mb-5">
                  {tab("prospect", "Prospect", "Org only. No login, no billing.")}
                  {tab("active", "Active account", "Creates the owner login + sets pricing.")}
                </div>

                <div className="grid grid-cols-2 gap-3.5">
                  <Field label="Business name" testid="cm-business" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} placeholder="Ora Coffee" full={mode === "prospect"} />
                  {mode === "active" && <Field label="Owner name" testid="cm-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jamie Smith" />}
                  <Field label={mode === "prospect" ? "Contact email" : "Email"} testid="cm-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jamie@oracoffee.com.au" full optional={mode === "prospect"} />
                  <Field label="Phone" testid="cm-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+61 412 345 678" optional />
                  <Field label="Website" testid="cm-website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="oracoffee.com.au" optional />
                </div>

                {mode === "active" && (
                  <>
                    <PricingControls value={terms} onChange={setTerms} testPrefix="cm" />
                    <label className="mt-4 flex items-start gap-2.5 text-[13px] cursor-pointer select-none">
                      <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="cm-send-email" className="mt-[3px]" />
                      <span className="text-[#3a3d44]">Email login credentials to the owner</span>
                    </label>
                  </>
                )}
                {mode === "prospect" && (
                  <p className="mt-4 text-[12.5px] text-[#3a3d44]/70 leading-relaxed bg-[#6474FF]/[0.07] border border-[#6474FF]/15 rounded-xl px-3.5 py-3">
                    Creates the business with no login and no billing. Design the pass now — activate later to set pricing and create the owner account.
                  </p>
                )}

                <div className="mt-7 flex items-center gap-3">
                  <button data-testid="cm-submit" disabled={busy} onClick={submit}
                    className="btn-lift bg-[#C8FF4D] text-[#0B0D10] rounded-full h-11 px-6 font-semibold text-[14px] disabled:opacity-60 inline-flex items-center gap-2">
                    {busy ? <><Loader2 size={14} className="animate-spin" />Creating…</> : (mode === "prospect" ? <><Wand2 size={14} />Create prospect</> : <><Check size={14} />Create account</>)}
                  </button>
                  <button onClick={onClose} className="text-[13px] text-[#3a3d44] hover:text-[#0B0D10]">Cancel</button>
                </div>
              </>
            ) : result.mode === "prospect" ? (
              <div data-testid="cm-prospect-result">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#C8FF4D] grid place-items-center mb-5"><Check size={26} strokeWidth={3} className="text-[#0B0D10]" /></div>
                <h3 className="font-display font-extrabold text-[24px] tracking-[-0.02em]">Prospect created.</h3>
                <p className="text-[13.5px] text-[#3a3d44] mt-2 leading-relaxed">
                  <strong>{result.business_name}</strong> — no login, no billing. Activate it from the list when they sign.
                </p>
                <div className="mt-6 flex gap-2.5">
                  <button data-testid="cm-design-now" onClick={() => onDesign(result.org_id, result.business_name)}
                    className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[13.5px] inline-flex items-center gap-1.5">
                    Design pass <ArrowRight size={14} />
                  </button>
                  <button onClick={onClose} className="text-[13px] text-[#3a3d44] hover:text-[#0B0D10] px-3">Done</button>
                </div>
              </div>
            ) : (
              <div data-testid="cm-result">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#C8FF4D] grid place-items-center mb-5"><Check size={26} strokeWidth={3} className="text-[#0B0D10]" /></div>
                <h3 className="font-display font-extrabold text-[24px] tracking-[-0.02em]">Account created.</h3>
                <p className="text-[13.5px] text-[#3a3d44] mt-2">Login details {result.email_sent ? "have been emailed to the owner" : "are below — copy them before closing"}.</p>
                <CredentialsCard result={result} />
                <div className="mt-6 flex gap-2">
                  <button onClick={onClose} className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[13.5px]">Done</button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------- Activate modal: prospect → paying merchant ----------
function ActivateMerchantModal({ org, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (org) {
      setForm({ name: "", email: org.contact_email || "", phone: "" });
      setTerms(DEFAULT_TERMS); setResult(null); setSendEmail(true);
    }
  }, [org]);

  if (!org) return null;

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Owner name and email are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/merchants/${org.id}/activate`, {
        name: form.name, email: form.email.trim(), phone: form.phone || null,
        send_welcome_email: sendEmail, ...terms,
      });
      setResult(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0B0D10]/60 backdrop-blur-sm"
        onClick={onClose} data-testid="activate-merchant-modal">
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[560px] max-h-[92vh] overflow-y-auto bg-[#F4F1E9] rounded-[24px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)]">

          <div className="bg-[#0B0D10] text-white px-6 py-5 flex items-center justify-between sticky top-0 z-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#C8FF4D]">Admin</div>
              <div className="font-display font-extrabold text-[19px] tracking-tight mt-0.5">Activate {org.name}</div>
            </div>
            <button data-testid="activate-merchant-close" onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center bg-white/[0.08] hover:bg-white/[0.15]"><X size={16} /></button>
          </div>

          <div className="p-6 sm:p-8">
            {!result ? (
              <>
                <p className="text-[13.5px] text-[#3a3d44] leading-relaxed mb-5">Set pricing and create the owner login. Everything already built stays as-is.</p>
                <div className="grid grid-cols-2 gap-3.5">
                  <Field label="Owner name" testid="am-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jamie Smith" />
                  <Field label="Phone" testid="am-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+61 412 345 678" optional />
                  <Field label="Email" testid="am-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jamie@oracoffee.com.au" full />
                </div>
                <PricingControls value={terms} onChange={setTerms} testPrefix="am" />
                <label className="mt-4 flex items-start gap-2.5 text-[13px] cursor-pointer select-none">
                  <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="am-send-email" className="mt-[3px]" />
                  <span className="text-[#3a3d44]">Email login credentials to the owner</span>
                </label>
                <div className="mt-7 flex items-center gap-3">
                  <button data-testid="am-submit" disabled={busy} onClick={submit}
                    className="btn-lift bg-[#C8FF4D] text-[#0B0D10] rounded-full h-11 px-6 font-semibold text-[14px] disabled:opacity-60 inline-flex items-center gap-2">
                    {busy ? <><Loader2 size={14} className="animate-spin" />Activating…</> : <><KeyRound size={14} />Activate merchant</>}
                  </button>
                  <button onClick={onClose} className="text-[13px] text-[#3a3d44] hover:text-[#0B0D10]">Cancel</button>
                </div>
              </>
            ) : (
              <div data-testid="am-result">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#C8FF4D] grid place-items-center mb-5"><Check size={26} strokeWidth={3} className="text-[#0B0D10]" /></div>
                <h3 className="font-display font-extrabold text-[24px] tracking-[-0.02em]">{org.name} is active.</h3>
                <p className="text-[13.5px] text-[#3a3d44] mt-2">Login details {result.email_sent ? "have been emailed to the owner" : "are below — copy them before closing"}.</p>
                <CredentialsCard result={result} />
                <div className="mt-6 flex gap-2">
                  <button onClick={onClose} className="btn-lift bg-[#0B0D10] text-white rounded-full h-11 px-5 font-semibold text-[13.5px]">Done</button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, optional, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium">
        {label} {optional && <span className="normal-case tracking-normal text-[#3a3d44]/50 font-normal">Optional</span>}
      </span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] focus:outline-none px-3.5 h-11 text-[14px]" />
    </label>
  );
}

// Short human-friendly age string ("3d ago", "just now", "2mo ago").
function relativeTime(iso) {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diff = Math.max(0, Date.now() - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 30 * 86400) return `${Math.round(diff / 86400)}d ago`;
    if (diff < 365 * 86400) return `${Math.round(diff / (30 * 86400))}mo ago`;
    return `${Math.round(diff / (365 * 86400))}y ago`;
  } catch { return null; }
}
